import { useCallback, useEffect, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import axios from "axios"
import { useTranslation } from "react-i18next"
import { ArrowRightLeft, BedDouble, Building2, LogOut, Trash2, Wrench } from "lucide-react"
import AppLayout from "@/components/app/AppLayout"
import { FeatureGate } from "@/components/app/FeatureGate"
import { PilotBanner } from "@/components/app/PilotBanner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Switch } from "@/components/ui/switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { API_BASE } from "@/config/api"
import { formatDate } from "@/lib/format"
import { opsErrorMessage } from "@/lib/ops-error"
import { useEntitlements } from "@/hooks/use-entitlements"
import { toast } from "sonner"

const API = API_BASE

export default function Adt() {
  const { t } = useTranslation()
  const { hasFeature } = useEntitlements()
  const canNetwork = hasFeature("multi_location")
  const [searchParams, setSearchParams] = useSearchParams()
  const [wards, setWards] = useState([])
  const [summary, setSummary] = useState({ total_beds: 0, occupied_beds: 0, available_beds: 0 })
  const [admissions, setAdmissions] = useState([])
  const [clients, setClients] = useState([])
  const [beds, setBeds] = useState([])
  const [transferBeds, setTransferBeds] = useState([])
  const [allSites, setAllSites] = useState(false)
  const [loading, setLoading] = useState(true)
  const [selectedWardId, setSelectedWardId] = useState("")
  const [wardForm, setWardForm] = useState({ name: "", code: "", bed_count: 10, active: true })
  const [editingWardId, setEditingWardId] = useState(null)
  const [admitForm, setAdmitForm] = useState({ client_id: "", bed_id: "", notes: "", queue_ticket_id: "" })
  const [transferFor, setTransferFor] = useState(null)
  const [transferBedId, setTransferBedId] = useState("")

  const loadWards = useCallback(async () => {
    const { data } = await axios.get(`${API}/adt/wards`, { params: allSites ? { all_sites: true } : {} })
    setWards(data.wards || [])
    setSummary(data.summary || { total_beds: 0, occupied_beds: 0, available_beds: 0 })
    return data.wards || []
  }, [allSites])

  const loadAdmissions = useCallback(async () => {
    const { data } = await axios.get(`${API}/admissions`)
    setAdmissions(data || [])
  }, [])

  const loadBeds = useCallback(async (wardId) => {
    if (!wardId) {
      setBeds([])
      return
    }
    const { data } = await axios.get(`${API}/adt/wards/${wardId}/beds`)
    setBeds(data || [])
  }, [])

  const loadTransferBeds = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/adt/beds/available`, {
        params: allSites ? { all_sites: true } : {},
      })
      setTransferBeds(data || [])
    } catch {
      setTransferBeds([])
    }
  }, [allSites])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const w = await loadWards()
      await loadAdmissions()
      await loadTransferBeds()
      const { data: clientData } = await axios.get(`${API}/clients`)
      setClients(clientData || [])
      if (!selectedWardId && w.length) {
        const active = w.find((x) => x.active !== false) || w[0]
        setSelectedWardId(active.id)
      }
    } catch {
      toast.error(t("app.adt.toast.loadFailed"))
    } finally {
      setLoading(false)
    }
  }, [loadWards, loadAdmissions, loadTransferBeds, selectedWardId, t])

  useEffect(() => {
    load()
  }, [allSites])

  useEffect(() => {
    loadBeds(selectedWardId)
  }, [selectedWardId, loadBeds])

  useEffect(() => {
    const client = searchParams.get("client")
    const ticket = searchParams.get("ticket")
    if (!client && !ticket) return
    setAdmitForm((f) => ({
      ...f,
      client_id: client || f.client_id,
      queue_ticket_id: ticket || f.queue_ticket_id,
    }))
    const next = new URLSearchParams(searchParams)
    next.delete("client")
    next.delete("ticket")
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const resetWardForm = () => {
    setWardForm({ name: "", code: "", bed_count: 10, active: true })
    setEditingWardId(null)
  }

  const saveWard = async () => {
    if (!wardForm.name.trim()) return toast.error(t("app.adt.toast.wardNameRequired"))
    const bed_count = Number(wardForm.bed_count)
    if (!bed_count || bed_count < 1) return toast.error(t("app.adt.toast.invalidBedCount"))
    try {
      if (editingWardId) {
        await axios.patch(`${API}/adt/wards/${editingWardId}`, {
          name: wardForm.name.trim(),
          code: wardForm.code.trim(),
          bed_count,
          active: wardForm.active,
        })
      } else {
        await axios.post(`${API}/adt/wards`, {
          name: wardForm.name.trim(),
          code: wardForm.code.trim(),
          bed_count,
          active: wardForm.active,
        })
      }
      resetWardForm()
      const w = await loadWards()
      if (!selectedWardId && w.length) setSelectedWardId(w[0].id)
      toast.success(editingWardId ? t("app.adt.toast.wardUpdated") : t("app.adt.toast.wardAdded"))
    } catch (err) {
      toast.error(opsErrorMessage(err, t, "app.adt.toast.saveFailed"))
    }
  }

  const startEditWard = (w) => {
    setEditingWardId(w.id)
    setSelectedWardId(w.id)
    setWardForm({
      name: w.name,
      code: w.code || "",
      bed_count: w.bed_count || 10,
      active: w.active !== false,
    })
  }

  const deleteWard = async (id) => {
    try {
      await axios.delete(`${API}/adt/wards/${id}`)
      if (selectedWardId === id) setSelectedWardId("")
      if (editingWardId === id) resetWardForm()
      await loadWards()
      toast.success(t("app.adt.toast.wardDeleted"))
    } catch (err) {
      toast.error(opsErrorMessage(err, t, "app.adt.toast.saveFailed"))
    }
  }

  const toggleBedStatus = async (bed) => {
    if (bed.status === "occupied") return
    const next = bed.status === "maintenance" ? "available" : "maintenance"
    try {
      await axios.patch(`${API}/adt/beds/${bed.id}`, { status: next })
      await Promise.all([loadBeds(selectedWardId), loadWards(), loadTransferBeds()])
      toast.success(
        next === "maintenance"
          ? t("app.adt.toast.bedMaintenance")
          : t("app.adt.toast.bedAvailable")
      )
    } catch (err) {
      toast.error(opsErrorMessage(err, t, "app.adt.toast.saveFailed"))
    }
  }

  const admit = async () => {
    if (!admitForm.client_id && !admitForm.queue_ticket_id) {
      return toast.error(t("app.adt.toast.clientRequired"))
    }
    if (!admitForm.bed_id) return toast.error(t("app.adt.toast.bedRequired"))
    try {
      await axios.post(`${API}/admissions`, {
        client_id: admitForm.client_id || null,
        bed_id: admitForm.bed_id,
        notes: admitForm.notes,
        queue_ticket_id: admitForm.queue_ticket_id || null,
      })
      setAdmitForm({ client_id: "", bed_id: "", notes: admitForm.notes, queue_ticket_id: "" })
      await Promise.all([loadAdmissions(), loadWards(), loadBeds(selectedWardId), loadTransferBeds()])
      toast.success(t("app.adt.toast.admitted"))
    } catch (err) {
      toast.error(opsErrorMessage(err, t, "app.adt.toast.saveFailed"))
    }
  }

  const discharge = async (id) => {
    try {
      await axios.patch(`${API}/admissions/${id}/discharge`)
      await Promise.all([loadAdmissions(), loadWards(), loadBeds(selectedWardId), loadTransferBeds()])
      toast.success(t("app.adt.toast.discharged"))
    } catch (err) {
      toast.error(opsErrorMessage(err, t, "app.adt.toast.saveFailed"))
    }
  }

  const openTransfer = async (admission) => {
    setTransferFor(admission)
    setTransferBedId("")
    await loadTransferBeds()
  }

  const confirmTransfer = async () => {
    if (!transferFor || !transferBedId) return toast.error(t("app.adt.toast.bedRequired"))
    try {
      await axios.post(`${API}/admissions/${transferFor.id}/transfer`, { bed_id: transferBedId })
      setTransferFor(null)
      setTransferBedId("")
      await Promise.all([loadAdmissions(), loadWards(), loadBeds(selectedWardId), loadTransferBeds()])
      toast.success(t("app.adt.toast.transferred"))
    } catch (err) {
      toast.error(opsErrorMessage(err, t, "app.adt.toast.saveFailed"))
    }
  }

  const activeAdmissions = admissions.filter((a) => a.status === "admitted")
  const selectedWard = wards.find((w) => w.id === selectedWardId)
  const selectedWardActive = selectedWard?.active !== false
  const availableBeds = beds.filter((b) => b.status === "available")
  const transferOptions = transferBeds.filter((b) => b.id !== transferFor?.bed_id)

  return (
    <FeatureGate
      feature="adt"
      title={t("app.adt.gateTitle")}
      description={t("app.adt.gateDescription")}
    >
      <AppLayout title={t("app.adt.title")}>
        <p className="text-xs text-muted-foreground mb-4 max-w-2xl">
          {t("app.adt.intro")}{" "}
          <Link to="/app/queues" className="underline underline-offset-2">
            {t("app.nav.queues")}
          </Link>
          {" · "}
          <Link to="/app/settings" className="underline underline-offset-2">
            {t("app.nav.settings")}
          </Link>
        </p>

        {!loading && !wards.length && (
          <PilotBanner className="mb-4 max-w-2xl">{t("app.adt.setupHint")}</PilotBanner>
        )}

        {canNetwork && (
          <label className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
            <input type="checkbox" checked={allSites} onChange={(e) => setAllSites(e.target.checked)} />
            {t("app.ops.allSites", { defaultValue: "Toate locațiile" })}
          </label>
        )}

        <div className="grid sm:grid-cols-3 gap-3 mb-6 max-w-3xl">
          <article className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs text-muted-foreground">{t("app.adt.metrics.totalBeds")}</p>
            <p className="font-display text-2xl mt-1">{summary.total_beds}</p>
          </article>
          <article className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs text-muted-foreground">{t("app.adt.metrics.occupied")}</p>
            <p className="font-display text-2xl mt-1">{summary.occupied_beds}</p>
          </article>
          <article className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs text-muted-foreground">{t("app.adt.metrics.available")}</p>
            <p className="font-display text-2xl mt-1">{summary.available_beds}</p>
          </article>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-5xl">
          <div className="space-y-6">
            <section className="rounded-xl border border-border p-6 bg-card space-y-4">
              <h3 className="text-sm font-medium flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                {editingWardId ? t("app.adt.editWard") : t("app.adt.addWard")}
              </h3>
              <p className="text-xs text-muted-foreground">{t("app.adt.wardHelp")}</p>
              <div>
                <Label className="text-xs">{t("app.adt.fieldWardName")}</Label>
                <Input
                  className="mt-1"
                  value={wardForm.name}
                  onChange={(e) => setWardForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder={t("app.adt.wardNamePlaceholder")}
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t("app.adt.fieldWardCode")}</Label>
                  <Input
                    className="mt-1"
                    value={wardForm.code}
                    onChange={(e) => setWardForm((f) => ({ ...f, code: e.target.value }))}
                    placeholder="A1"
                  />
                </div>
                <div>
                  <Label className="text-xs">{t("app.adt.fieldBedCount")}</Label>
                  <Input
                    type="number"
                    min={1}
                    max={200}
                    className="mt-1"
                    value={wardForm.bed_count}
                    onChange={(e) => setWardForm((f) => ({ ...f, bed_count: Number(e.target.value) }))}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                <Label className="text-xs">{t("app.adt.fieldWardActive")}</Label>
                <Switch
                  checked={wardForm.active}
                  onCheckedChange={(v) => setWardForm((f) => ({ ...f, active: v }))}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button className="rounded-full" onClick={saveWard}>
                  {editingWardId ? t("app.adt.saveWard") : t("app.adt.addWardBtn")}
                </Button>
                {editingWardId && (
                  <Button variant="ghost" className="rounded-full" onClick={resetWardForm}>
                    {t("app.adt.cancel")}
                  </Button>
                )}
              </div>
            </section>

            <ul className="divide-y divide-border rounded-xl border border-border bg-card text-sm">
              {loading && (
                <li className="px-4 py-8 text-center text-muted-foreground">{t("app.adt.loading")}</li>
              )}
              {!loading &&
                wards.map((w) => (
                  <li key={w.id} className="px-4 py-3 flex flex-wrap items-center justify-between gap-2">
                    <button
                      type="button"
                      className={`text-left flex-1 min-w-0 rounded-lg px-2 py-1 -mx-2 ${
                        selectedWardId === w.id ? "bg-secondary" : "hover:bg-muted"
                      }`}
                      onClick={() => {
                        setSelectedWardId(w.id)
                        setAdmitForm((f) => ({ ...f, bed_id: "" }))
                      }}
                    >
                      <span className="font-medium">{w.name}</span>
                      {w.code && <span className="text-muted-foreground text-xs ml-1">({w.code})</span>}
                      {w.active === false && (
                        <Badge variant="secondary" className="ml-2 text-[10px]">
                          {t("app.adt.inactive")}
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground block mt-0.5">
                        {t("app.adt.wardOccupancy", {
                          occupied: w.occupied_beds || 0,
                          total: w.total_beds || 0,
                        })}
                      </span>
                    </button>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="sm" className="rounded-full h-8" onClick={() => startEditWard(w)}>
                        {t("app.adt.edit")}
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{t("app.adt.deleteWardTitle")}</AlertDialogTitle>
                            <AlertDialogDescription>{t("app.adt.deleteWardBody")}</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t("app.adt.cancel")}</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteWard(w.id)}>
                              {t("app.adt.confirmDelete")}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </li>
                ))}
              {!loading && !wards.length && (
                <li className="px-4 py-8 text-center text-muted-foreground">{t("app.adt.noWards")}</li>
              )}
            </ul>

            {selectedWardId && (
              <section className="rounded-xl border border-border p-4 bg-card">
                <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <BedDouble className="h-4 w-4" />
                  {t("app.adt.bedsInWard")}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {beds.map((b) => (
                    <div key={b.id} className="inline-flex items-center gap-1">
                      <Badge
                        variant={
                          b.status === "occupied"
                            ? "destructive"
                            : b.status === "maintenance"
                              ? "secondary"
                              : "outline"
                        }
                        title={b.patient_name || undefined}
                      >
                        {b.label}
                        {b.patient_name ? ` · ${b.patient_name}` : ""}
                        {b.status === "maintenance" ? ` · ${t("app.adt.maintenance")}` : ""}
                      </Badge>
                      {b.status !== "occupied" && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          title={
                            b.status === "maintenance"
                              ? t("app.adt.markAvailable")
                              : t("app.adt.markMaintenance")
                          }
                          onClick={() => toggleBedStatus(b)}
                        >
                          <Wrench className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  ))}
                  {!beds.length && (
                    <p className="text-xs text-muted-foreground">{t("app.adt.noBeds")}</p>
                  )}
                </div>
              </section>
            )}
          </div>

          <div className="space-y-6">
            <section className="rounded-xl border border-border p-6 bg-card space-y-3">
              <h3 className="text-sm font-medium">{t("app.adt.newAdmission")}</h3>
              <div>
                <Label className="text-xs">{t("app.adt.fieldClient")}</Label>
                <Select
                  value={admitForm.client_id}
                  onValueChange={(v) => setAdmitForm((f) => ({ ...f, client_id: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={t("app.adt.selectClient")} />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{t("app.adt.fieldBed")}</Label>
                <Select
                  value={admitForm.bed_id}
                  onValueChange={(v) => setAdmitForm((f) => ({ ...f, bed_id: v }))}
                  disabled={!selectedWardId || !selectedWardActive || !availableBeds.length}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={t("app.adt.selectBed")} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableBeds.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {t("app.adt.bedOption", { label: b.label })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedWardId && !selectedWardActive && (
                  <p className="text-xs text-amber-600 mt-1">{t("app.adt.wardInactiveHint")}</p>
                )}
                {selectedWardId && selectedWardActive && !availableBeds.length && (
                  <p className="text-xs text-amber-600 mt-1">{t("app.adt.noAvailableBeds")}</p>
                )}
              </div>
              <div>
                <Label className="text-xs">{t("app.adt.fieldNotes")}</Label>
                <Input
                  className="mt-1"
                  value={admitForm.notes}
                  onChange={(e) => setAdmitForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </div>
              <Button className="rounded-full" onClick={admit} disabled={!wards.length}>
                {t("app.adt.admitBtn")}
              </Button>
            </section>

            <section>
              <h3 className="text-sm font-medium mb-2">{t("app.adt.activeAdmissions")}</h3>
              <ul className="divide-y divide-border rounded-xl border border-border bg-card text-sm">
                {activeAdmissions.map((a) => (
                  <li key={a.id} className="px-4 py-3 flex flex-wrap justify-between items-center gap-2">
                    <div>
                      <p className="font-medium">{a.client_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {a.ward_name || a.ward} / {a.bed_label || a.bed} · {formatDate(a.admitted_at)}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full gap-1"
                        onClick={() => openTransfer(a)}
                      >
                        <ArrowRightLeft className="h-3.5 w-3.5" />
                        {t("app.adt.transferBtn")}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full gap-1"
                        onClick={() => discharge(a.id)}
                      >
                        <LogOut className="h-3.5 w-3.5" />
                        {t("app.adt.dischargeBtn")}
                      </Button>
                    </div>
                  </li>
                ))}
                {!activeAdmissions.length && (
                  <li className="px-4 py-8 text-center text-muted-foreground">{t("app.adt.noActive")}</li>
                )}
              </ul>
            </section>

            {admissions.some((a) => a.status === "discharged") && (
              <section>
                <h3 className="text-sm font-medium mb-2">{t("app.adt.recentDischarges")}</h3>
                <ul className="divide-y divide-border rounded-xl border border-border bg-card text-sm max-h-48 overflow-auto">
                  {admissions
                    .filter((a) => a.status === "discharged")
                    .slice(0, 10)
                    .map((a) => (
                      <li key={a.id} className="px-4 py-2 text-muted-foreground">
                        {a.client_name} — {a.ward_name || a.ward} / {a.bed_label || a.bed}
                      </li>
                    ))}
                </ul>
              </section>
            )}
          </div>
        </div>

        <Dialog open={Boolean(transferFor)} onOpenChange={(o) => !o && setTransferFor(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{t("app.adt.transferTitle")}</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-muted-foreground">
              {transferFor?.client_name} — {transferFor?.ward_name || transferFor?.ward} /{" "}
              {transferFor?.bed_label || transferFor?.bed}
            </p>
            <div>
              <Label className="text-xs">{t("app.adt.fieldBed")}</Label>
              <Select value={transferBedId} onValueChange={setTransferBedId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t("app.adt.selectBed")} />
                </SelectTrigger>
                <SelectContent>
                  {transferOptions.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.ward_name} / {t("app.adt.bedOption", { label: b.label })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!transferOptions.length && (
                <p className="text-xs text-amber-600 mt-1">{t("app.adt.noTransferBeds")}</p>
              )}
            </div>
            <Button className="rounded-full w-full" onClick={confirmTransfer} disabled={!transferBedId}>
              {t("app.adt.transferConfirm")}
            </Button>
          </DialogContent>
        </Dialog>
      </AppLayout>
    </FeatureGate>
  )
}
